import ApiService from "../../../services/ApiService";
import { resolveApiFetchUrl } from "../../../services/ApiBaseUrl";
import { apiFetch } from "../../../services/apiFetch";
import AuthService from "../../../services/AuthService";
import { WORD_WHEEL_APP_CODE, WORD_WHEEL_SEASON } from "./constants";

async function optionalAuthHeaders() {
    const headers = new Headers();
    headers.set("Content-Type", "application/json");
    try {
        await ApiService.getAccessToken();
        const bearer = ApiService.init.headers.get("Authorization");
        if (bearer) {
            headers.set("Authorization", bearer);
        }
    } catch {
        /* guest */
    }
    return headers;
}

async function parseJsonResponse(result) {
    const json = await result.json();
    if (!result.ok) {
        const error = new Error(json.message || json.error || `HTTP ${result.status}: ${result.statusText}`);
        error.status = result.status;
        error.response = json;
        throw error;
    }
    return json;
}

async function getOptionalAuth(url, param) {
    const headers = await optionalAuthHeaders();
    let queryString = url;
    if (param) {
        const entities = [];
        for (const [key, value] of Object.entries(param)) {
            if (value || value === 0) {
                entities.push(`${key}=${encodeURIComponent(value)}`);
            }
        }
        if (entities.length > 0) {
            queryString = `${url}?${entities.join("&")}`;
        }
    }
    const result = await apiFetch(resolveApiFetchUrl(queryString), { method: "GET", headers });
    return parseJsonResponse(result);
}

async function postOptionalAuth(url, data) {
    const headers = await optionalAuthHeaders();
    const result = await apiFetch(resolveApiFetchUrl(url), {
        method: "POST",
        headers,
        body: JSON.stringify(data),
    });
    return parseJsonResponse(result);
}

async function putOptionalAuth(url, data) {
    const headers = await optionalAuthHeaders();
    const result = await apiFetch(resolveApiFetchUrl(url), {
        method: "PUT",
        headers,
        body: JSON.stringify(data),
    });
    return parseJsonResponse(result);
}

const WordWheelService = {
    APP_CODE: WORD_WHEEL_APP_CODE,
    SEASON: WORD_WHEEL_SEASON,

    isLoggedIn: async () => {
        try {
            return Boolean(await AuthService.isAuthenticated());
        } catch {
            return false;
        }
    },

    fetchById: (tplId) => getOptionalAuth(`/v1/puzzle/wordwheel/${encodeURIComponent(tplId)}`),

    fetchNext: () => getOptionalAuth("/v1/puzzle/wordwheel/next"),

    fetchJourneyLevel: (level, season = WORD_WHEEL_SEASON) => {
        const param = { level: String(level) };
        if (season) param.season = season;
        return getOptionalAuth("/v1/puzzle/wordwheel/journey", param);
    },

    fetchDaily: (date) => {
        const param = {};
        if (date) param.date = date;
        return getOptionalAuth("/v1/puzzle/wordwheel/daily", Object.keys(param).length ? param : undefined);
    },

    fetchOwnedPacks: async () => {
        const result = await getOptionalAuth("/v1/puzzle/wordwheel/owned-packs");
        return Array.isArray(result?.packs) ? result.packs : [];
    },

    fetchCoinsCatalog: () => getOptionalAuth("/v1/puzzle/wordwheel/coins-catalog"),

    startPlay: (wordWheelTplId) => postOptionalAuth("/v1/puzzle/wordwheel-play/start", { wordWheelTplId }),

    updateProgress: (wordWheelTplId, foundWords, bonusWords) =>
        putOptionalAuth("/v1/puzzle/wordwheel-play/progress", {
            wordWheelTplId,
            foundWords,
            ...(Array.isArray(bonusWords) ? { bonusWords } : {}),
        }),

    listDailyPlays: async ({ page = 0, pageSize = 10, sort = "updatedAt:desc", isCompleted } = {}) => {
        const params = new URLSearchParams({
            page: String(page),
            pageSize: String(pageSize),
            sort: String(sort),
        });
        if (isCompleted !== undefined && isCompleted !== null) {
            params.set("isCompleted", String(isCompleted));
        }
        return ApiService.getData(`/home/wordwheel-play?${params.toString()}`);
    },

    getCreditBalance: () =>
        ApiService.getData(`/home/credit/balance?appCode=${encodeURIComponent(WORD_WHEEL_APP_CODE)}`),

    consumeCredit: ({ featureUsed = "word_wheel_letter_reveal", creditsConsumed = 1 } = {}) =>
        ApiService.postData("/home/credit/consume", {
            appCode: WORD_WHEEL_APP_CODE,
            featureUsed,
            creditsConsumed,
        }),

    spendHintCoins: ({ coins = 10, featureUsed = "word_wheel_letter_hint" } = {}) =>
        ApiService.postData("/home/wordwheel/coins/spend", { coins, featureUsed }),

    peekDailyGift: () => ApiService.getData("/home/wordwheel/daily-gift"),

    claimDailyGift: () => ApiService.postData("/home/wordwheel/daily-gift", {}),
};

export function wordWheelPlayRoToCard(play) {
    const tpl = play?.wordWheelTpl || play?.template;
    const tplId = tpl?.id;
    if (!tplId && !play?.id) return null;
    return {
        id: tplId || play.id,
        playId: play.id,
        title: tpl?.title || "Daily Word Wheel",
        type: "WORD_WHEEL",
        isPremium: false,
        isCompleted: Boolean(play?.isCompleted),
    };
}

export default WordWheelService;
